import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import ABProcessMap from './ABProcessMap';
import ABNodeInsightsPanel from './ABNodeInsightsDrawer';
import ABSettingsModal from './ABSettingsModal';
import ABResultsPanel from './ABResultsPanel';

function getEntryInputKey(definition) {
  const nodes = definition?.definition?.nodes || definition?.nodes || [];
  const synapses = definition?.definition?.synapses || definition?.synapses || [];
  const targetNodes = new Set(synapses.map(s => s.target_node));
  const entryNodes = nodes.filter(n => !targetNodes.has(n.node_id));
  for (const node of entryNodes) {
    for (const inp of (node.inputs || [])) {
      if (inp.required && !inp.default) return inp.name;
    }
  }
  return 'case_data';
}

export default function ABTab({
  tab,
  definition,
  processId,
  abSessionId,
  onInputChange,
  onSettingsChange,
  onExecutionStarted,
  onExecutionFinished,
  execution,
  mapNodes,
}) {
  const [showSettings, setShowSettings] = useState(false);
  const [insightsNode, setInsightsNode] = useState(null);
  const [insightsData, setInsightsData] = useState(null);
  const prevIsRunning = useRef(execution.isRunning);
  const inputPopulated = useRef(false);

  // Reconnect to a known execution (running OR completed) when tab mounts
  const reconnectId = tab.executionId || tab.restoredExecutionId;
  useEffect(() => {
    if (reconnectId && !execution.executionId) {
      execution.reconnect(reconnectId);
    }
  }, [reconnectId, execution.executionId, execution.reconnect]);

  // Populate input from execution context for restored/reconnected tabs (once only)
  useEffect(() => {
    if (reconnectId && !inputPopulated.current && !tab.input && execution.executionState?.context) {
      const ctx = execution.executionState.context;
      const key = getEntryInputKey(definition);
      const restored = ctx[key] || ctx.case_data;
      if (restored) {
        inputPopulated.current = true;
        onInputChange(restored);
      }
    }
  }, [reconnectId, tab.input, execution.executionState, onInputChange, definition]);

  // Notify parent when execution transitions from running -> finished
  useEffect(() => {
    if (prevIsRunning.current && !execution.isRunning && execution.executionId) {
      onExecutionFinished?.();
    }
    prevIsRunning.current = execution.isRunning;
  }, [execution.isRunning, execution.executionId, onExecutionFinished]);

  const handleRun = useCallback(() => {
    if (!tab.input?.trim()) {
      alert('Please paste case data before running.');
      return;
    }

    const globalSNN = tab.settings?.globalSNN || {};
    const overrides = { ...(tab.settings?.nodeOverrides || {}) };

    // Auto-merge globalSNN into every non-pinned SNN node so the user
    // doesn't have to click "Apply to All SNN" before each run.
    (mapNodes || [])
      .filter((n) => n.data?.executionType === 'snn_pipeline')
      .forEach((n) => {
        const existing = overrides[n.id] || {};
        if (existing.pinned) return;
        const nodePS = existing.pipeline_settings || {};
        overrides[n.id] = {
          ...existing,
          pipeline_settings: { ...globalSNN, ...nodePS },
        };
      });

    const nodeOverrides = {};
    Object.entries(overrides).forEach(([nodeId, val]) => {
      const patch = { ...val };
      delete patch.pinned;
      if (Object.keys(patch).length > 0) nodeOverrides[nodeId] = patch;
    });

    const inputKey = getEntryInputKey(definition);
    execution.startExecution({
      processId,
      input: tab.input,
      inputs: { [inputKey]: tab.input },
      nodeOverrides: Object.keys(nodeOverrides).length > 0 ? nodeOverrides : null,
      abSessionId,
      tabLabel: tab.label,
    }).then((result) => {
      if (result?.executionId) {
        onExecutionStarted?.(result.executionId);
      }
    });
  }, [tab, processId, abSessionId, execution, mapNodes, onExecutionStarted]);

  const handleNodeClick = useCallback((nodeId, nodeData) => {
    setInsightsNode(nodeId);
    setInsightsData(nodeData);
  }, []);

  // Ranked list of nodes sorted by execution duration (descending)
  const slowestNodes = useMemo(() => {
    if (!execution.nodeResults) return [];
    return Object.entries(execution.nodeResults)
      .filter(([, r]) => r.duration_ms > 0)
      .sort((a, b) => b[1].duration_ms - a[1].duration_ms)
      .map(([nodeId, r]) => {
        const mapNode = mapNodes.find(n => n.id === nodeId);
        return {
          nodeId,
          label: mapNode?.data?.label || nodeId,
          executionType: mapNode?.data?.executionType || r.execution_type,
          typeLetter: mapNode?.data?.typeLetter || '?',
          duration_ms: r.duration_ms,
          status: r.status,
        };
      });
  }, [execution.nodeResults, mapNodes]);

  const [slowestOpen, setSlowestOpen] = useState(true);

  const timeoutLabel = useMemo(() => {
    const defObj = definition?.definition || definition;
    const secs = defObj?.global_timeout_seconds;
    if (!secs || secs <= 0) return null;
    return secs >= 60 ? `${Math.round(secs / 60)}m` : `${secs}s`;
  }, [definition]);

  return (
    <div className="ab-tab-content">
      {/* Top bar: input + controls */}
      <div className="ab-tab-top-bar">
        <textarea
          className="ab-input-area"
          placeholder="Paste case data here..."
          value={tab.input || ''}
          onChange={(e) => onInputChange(e.target.value)}
          disabled={execution.isRunning}
        />
        <div className="ab-tab-actions">
          {timeoutLabel && (
            <span className="ab-timeout-badge" title="Process timeout (set in process builder)">
              &#9201; {timeoutLabel}
            </span>
          )}
          <button className="ab-btn settings" onClick={() => setShowSettings(true)}>
            Settings
          </button>
          {execution.isRunning ? (
            <button
              className="ab-btn cancel"
              onClick={() => {
                if (window.confirm('Cancel this execution?')) {
                  execution.cancelExecution();
                }
              }}
            >
              &#9632; Cancel
            </button>
          ) : (
            <button className="ab-btn run" onClick={handleRun}>
              &#9654; Run
            </button>
          )}
        </div>
      </div>

      {execution.error && (
        <div className="ab-error-banner">{execution.error}</div>
      )}

      {/* Process map visualization */}
      <div className="ab-map-wrapper">
        <ABProcessMap
          definition={definition}
          executionState={execution.executionState}
          onNodeClick={handleNodeClick}
        />
      </div>

      {/* Slowest nodes ranking */}
      {slowestNodes.length > 0 && (
        <div className="ab-slowest-nodes">
          <div className="slowest-header" onClick={() => setSlowestOpen(!slowestOpen)}>
            <h3>{slowestOpen ? '\u25BE' : '\u25B8'} Slowest Nodes</h3>
          </div>
          {slowestOpen && (
            <div className="slowest-list">
              {slowestNodes.map((item, idx) => {
                const nodeData = mapNodes.find(n => n.id === item.nodeId)?.data;
                return (
                  <div
                    key={item.nodeId}
                    className={`slowest-row ${insightsNode === item.nodeId ? 'active' : ''}`}
                    onClick={() => handleNodeClick(item.nodeId, nodeData)}
                  >
                    <span className="slowest-rank">#{idx + 1}</span>
                    <span className={`type-badge ${item.executionType}`}>{item.typeLetter}</span>
                    <span className="slowest-name">{item.label}</span>
                    <span className={`status-pill ${item.status}`}>{item.status}</span>
                    <span className="slowest-duration">{(item.duration_ms / 1000).toFixed(2)}s</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Execution insights panel — full width below the map */}
      {insightsNode && (
        <ABNodeInsightsPanel
          nodeId={insightsNode}
          nodeData={insightsData}
          executionId={execution.executionId}
          onClose={() => { setInsightsNode(null); setInsightsData(null); }}
        />
      )}

      {/* Results panel */}
      <ABResultsPanel
        executionState={execution.executionState}
        nodeResults={execution.nodeResults}
        reportStatus={execution.reportStatus}
        executionId={execution.executionId}
      />

      {/* Settings modal */}
      {showSettings && (
        <ABSettingsModal
          settings={tab.settings || {}}
          onUpdate={(s) => onSettingsChange(s)}
          onClose={() => setShowSettings(false)}
          nodes={mapNodes}
        />
      )}
    </div>
  );
}
